import { useState } from 'react';
import { IMPORT_JSON_MESSAGES } from './messages';

export interface ImportExtra {
  key: string;
  onImport: (value: unknown) => void;
}

interface UseJsonImportParams {
  formFieldKeys: readonly string[];
  expectedProduct?: string;
  extras?: readonly ImportExtra[];
  onApplyFormFields: (picked: Record<string, unknown>) => void;
  onApplyRaw?: (raw: Record<string, unknown>) => void;
}

interface UseJsonImportResult {
  importText: string;
  setImportText: (text: string) => void;
  importError?: string;
  apply: () => boolean;
  reset: () => void;
}

export function useJsonImport({
  formFieldKeys,
  expectedProduct,
  extras,
  onApplyFormFields,
  onApplyRaw,
}: UseJsonImportParams): UseJsonImportResult {
  const [importText, setImportText] = useState('');
  const [importError, setImportError] = useState<string | undefined>();

  const apply = (): boolean => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(importText);
    } catch {
      setImportError(IMPORT_JSON_MESSAGES.invalidJson);
      return false;
    }

    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      setImportError(IMPORT_JSON_MESSAGES.notAnObject);
      return false;
    }

    const raw = parsed as Record<string, unknown>;

    if (expectedProduct && raw.product !== undefined && raw.product !== expectedProduct) {
      setImportError(IMPORT_JSON_MESSAGES.wrongProduct(String(raw.product), expectedProduct));
      return false;
    }

    const picked: Record<string, unknown> = {};
    formFieldKeys.forEach((key) => {
      if (key in raw) picked[key] = raw[key];
    });
    onApplyFormFields(picked);

    extras?.forEach((extra) => {
      if (extra.key in raw) extra.onImport(raw[extra.key]);
    });

    onApplyRaw?.(raw);

    setImportError(undefined);
    setImportText('');
    return true;
  };

  const reset = () => {
    setImportText('');
    setImportError(undefined);
  };

  return {
    importText, setImportText, importError, apply, reset,
  };
}
